import { useState, useRef, useEffect, useCallback } from 'react';
import { type Cliente } from '@/db';
import { useClientes } from '@/hooks/useDB';
import { useAuth } from '@/hooks/useAuth';
import { canAccessClient } from '@/auth/access';

interface ClienteAutocompleteProps {
  selected: Cliente | null;
  onSelect: (cliente: Cliente) => void;
  onClear: () => void;
}

export default function ClienteAutocomplete({ selected, onSelect, onClear }: ClienteAutocompleteProps) {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { clientes } = useClientes(debouncedQuery);
  const { user } = useAuth();

  const filteredClientes = debouncedQuery.trim()
    ? clientes.filter((cliente) => canAccessClient(user, cliente)).filter(
        (c) =>
          c.codigo.toLowerCase().includes(debouncedQuery.toLowerCase()) ||
          c.nome.toLowerCase().includes(debouncedQuery.toLowerCase()),
      ).slice(0, 15)
    : [];

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 200);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleSelect = useCallback(
    (cliente: Cliente) => {
      onSelect(cliente);
      setQuery('');
      setOpen(false);
    },
    [onSelect],
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open || filteredClientes.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((prev) => Math.min(prev + 1, filteredClientes.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && highlightIndex >= 0) {
      e.preventDefault();
      handleSelect(filteredClientes[highlightIndex]);
    }
  };

  if (selected) {
    return (
      <div className="flex items-center gap-2 px-3 py-2.5 bg-background-100 rounded-lg border border-background-200/70">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
              <span className="text-[10px] font-semibold text-primary-700">{selected.uf}</span>
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground-950 truncate">{selected.nome}</p>
              <p className="text-xs text-foreground-500">
                {selected.codigo} · {selected.cidade}/{selected.uf}
              </p>
            </div>
          </div>
        </div>
        {selected.alerta && (
          <span className="px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 text-[10px] font-semibold whitespace-nowrap">
            {selected.alerta}
          </span>
        )}
        <button
          onClick={onClear}
          className="w-7 h-7 flex items-center justify-center rounded-md text-foreground-400 hover:text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer"
        >
          <i className="ri-close-line" />
        </button>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            setHighlightIndex(-1);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder="Buscar cliente por código ou nome..."
          className="w-full pl-9 pr-3 py-2.5 text-sm bg-white border border-background-200/70 rounded-lg text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500/50 transition-all"
        />
      </div>

      {open && filteredClientes.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-background-200/70 rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto">
          {filteredClientes.map((cliente, idx) => (
            <button
              key={cliente.id}
              onClick={() => handleSelect(cliente)}
              className={`w-full text-left px-3 py-2.5 flex items-center gap-3 transition-colors cursor-pointer ${
                idx === highlightIndex
                  ? 'bg-primary-50'
                  : 'hover:bg-background-50'
              }`}
            >
              <div className="w-7 h-7 rounded-full bg-background-100 flex items-center justify-center flex-shrink-0">
                <span className="text-[10px] font-semibold text-foreground-600">{cliente.uf}</span>
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-foreground-950 truncate">{cliente.nome}</p>
                <p className="text-xs text-foreground-500">
                  {cliente.codigo} · {cliente.cidade}/{cliente.uf}
                </p>
              </div>
              {cliente.alerta && (
                <span className="px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 text-[10px] font-semibold whitespace-nowrap">
                  {cliente.alerta}
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {open && query.trim() && filteredClientes.length === 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-background-200/70 rounded-lg shadow-lg z-50 p-4 text-center">
          <p className="text-sm text-foreground-500">Nenhum cliente encontrado</p>
        </div>
      )}
    </div>
  );
}
